<template>
  <div class="geogebra-render-section">
    <div :class="['card', { 'readonly-mode': readOnly }]">
      <cardHeader
        :numOrder="getQuestionNumber() === 'N/A' ? numOrder : getQuestionNumber()"
        :elementType="currentElement.el_Type"
        :mode="'Render'"
        :isAgent="isAgent" />

      <div class="card-body">
        <!-- Main Question Text -->
        <div class="mb-2">
          <h3>{{ currentElement.el_Text }}</h3>
        </div>

        <!-- Rich Text Content -->
        <div class="quill-display" v-if="hasRichText" v-html="currentElement.el_RichText"></div>

        <!-- Feedback Display -->
        <div class="AllFeedbacks" v-if="showFeedback">
          <div v-if="isCorrect && currentElement.feedback?.correct" class="PositiveFeebackContainer">
            {{ currentElement.feedback.correct }}
          </div>
          <div v-else-if="!isCorrect && currentElement.feedback?.incorrect" class="NegativeFeebackContainer">
            {{ currentElement.feedback.incorrect }}
          </div>
        </div>

        <!-- GeoGebra Applet Container -->
        <div class="geogebra-wrapper mt-3 mb-3">
          <div v-if="ggbError" class="alert alert-danger">
            {{ ggbError }}
          </div>
          <div v-else-if="ggbLoading" class="text-center p-4">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading GeoGebra...</span>
            </div>
            <p class="mt-2">Loading GeoGebra applet...</p>
          </div>
          <div ref="ggbContainer" :class="containerId" class="geogebra-container"></div>
        </div>

        <!-- Hint Button -->
        <div class="hintContainer" v-if="displayHintButton && !readOnly">
          <button @click="HandlerShowHint" class="hintBT btn btn-primary btn-sm">
            Hint!
          </button>
          <div v-if="showHint" class="hintMessage">{{ currentElement.tip }}</div>
        </div>
      </div>

      <!-- Report Container -->
      <div class="ReportContainer" v-if="showReport">
        <div class="row">
          <div class="col">
            <h5>Results:</h5>
          </div>
          <div class="col" style="text-align: right;">
            <strong>Date:</strong> {{ currentDate }}
          </div>
          <hr />
          <p><strong>Your Answer:</strong> {{ responseData.answers || 'No answer provided' }}</p>
          <p><strong>Correct:</strong> <span :class="responseData.correct ? 'text-success' : 'text-danger'">{{ responseData.correct ? '✓ Yes' : '✗ No' }}</span></p>
          <p><strong>Score:</strong> {{ responseData.score }} / {{ currentElement.maxScore }}</p>
          <p v-if="hintUsed"><strong>Hint was used:</strong> Yes</p>
        </div>
      </div>

      <!-- Card Footer - Learning Mode -->
      <cardFooterLearningMode
        v-if="publicationStore.publicationData?.mode !== 'exam' && !readOnly"
        :showReport="showReport"
        :submitAnswers="submitResponse"
        :closeReport="closeReport"
        :resetSelections="resetInteraction" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, onUnmounted } from 'vue'
import cardHeader from './cardHeader.vue'
import cardFooterLearningMode from './cardFooterLearningMode.vue'
import { useResponsesStore } from '../../stores/useResponsesStore'
import { usePublicationStore } from '@/stores/usePublicationStore'
import { useGeoGebra } from '../../composables/useGeoGebra.js'

const props = defineProps({
  question: { type: Object, required: true },
  testData: { type: Object, required: true },
  restoredAnswer: { type: Object, default: () => ({}) },
  numOrder: { type: [Number, null], default: null },
  questionIndexMap: { type: Object, required: false },
  readOnly: { type: Boolean, default: false },
  isAgent: { type: Boolean, default: false },
  showFeedback: { type: Boolean, default: false }
})

const emit = defineEmits(['response-saved'])

// Stores
const responsesStore = useResponsesStore()
const publicationStore = usePublicationStore()

// Get question number from index map
const getQuestionNumber = () => {
  if (!props.questionIndexMap || typeof props.questionIndexMap !== 'object') {
    console.error("❌ questionIndexMap est indéfini ou n'est pas un objet.");
    return "N/A";
  }
  return props.questionIndexMap[props.question.el_ID] ?? "N/A";
}

// Refs
const ggbContainer = ref(null)
const currentElement = ref(props.question)
const containerId = ref(`ggb-render-${Date.now()}`)

// State
const showHint = ref(false)
const hintUsed = ref(false)
const showReport = ref(false)
const hasSubmitted = ref(false)
const currentDate = ref(new Date().toLocaleString())

// Initialize responseData with restoredAnswer if available
const responseData = ref({
  answers: props.restoredAnswer?.answers || '',
  correct: props.restoredAnswer?.correct || false,
  score: props.restoredAnswer?.score || 0,
  base64Data: props.restoredAnswer?.base64Data || '',
  hintUsed: props.restoredAnswer?.hintUsed || false
})

// GeoGebra
const {
  appletInstance,
  isReady: ggbReady,
  isLoading: ggbLoading,
  error: ggbError,
  initApplet,
  getBase64,
  getValue,
  getValueString,
  reset,
  removeApplet
} = useGeoGebra()

// Computed
const hasRichText = computed(() => {
  return currentElement.value?.el_RichText && currentElement.value.el_RichText.trim() !== ''
})

const displayHintButton = computed(() => {
  return currentElement.value?.tip && currentElement.value.tip.trim() !== ''
})

const isCorrect = computed(() => responseData.value.correct)

// Methods
const HandlerShowHint = () => {
  showHint.value = !showHint.value
  if (!hintUsed.value) {
    hintUsed.value = true
    // Save hint usage
    responsesStore.setHintUsed(currentElement.value.el_ID, true)
  }
}

const submitResponse = async () => {
  console.log('🔵 GeoGebra Submit clicked', {
    ggbReady: ggbReady.value,
    hasSubmitted: hasSubmitted.value,
    showReport: showReport.value
  })

  if (!ggbReady.value) {
    console.warn('⚠️ GeoGebra not ready yet')
    alert('Please wait for GeoGebra to load completely')
    return
  }

  if (hasSubmitted.value) {
    console.log('✅ Already submitted, toggling report')
    showReport.value = !showReport.value
    return
  }

  const variables = currentElement.value?.scoring?.variables || {}

  try {
    // Récupérer les 3 variables de GeoGebra : correct, score, answers
    // Les noms des variables peuvent être configurés dans l'éditeur
    const correctVarName = variables.correct || 'correct'
    const scoreVarName = variables.score || 'score'
    const answersVarName = variables.answers || 'answers'

    console.log('📊 Reading GeoGebra variables:', {
      correctVar: correctVarName,
      scoreVar: scoreVarName,
      answersVar: answersVarName
    })

    // Récupérer les valeurs depuis GeoGebra
    const correctValue = getValue(correctVarName)
    const scoreValue = getValue(scoreVarName)
    const answersValue = getValueString(answersVarName)

    console.log('📊 GeoGebra values retrieved:', {
      correct: correctValue,
      score: scoreValue,
      answers: answersValue
    })

    // Normaliser la valeur 'correct' (peut être 0/1, true/false, "true"/"false")
    let isCorrect = false
    if (typeof correctValue === 'boolean') {
      isCorrect = correctValue
    } else if (typeof correctValue === 'number') {
      isCorrect = correctValue === 1
    } else if (typeof correctValue === 'string') {
      isCorrect = correctValue.toLowerCase() === 'true'
    }

    // Normaliser le score
    let finalScore = 0
    if (typeof scoreValue === 'number' && !isNaN(scoreValue)) {
      finalScore = scoreValue
    } else if (isCorrect) {
      // Si pas de score mais correct=true, donner le maxScore
      finalScore = currentElement.value.maxScore || 1
    }

    responseData.value = {
      answers: answersValue || '',
      correct: isCorrect,
      score: finalScore,
      base64Data: ''
    }

    // Save Base64 if enabled
    if (currentElement.value.saveB64) {
      const base64 = await getBase64()
      responseData.value.base64Data = base64
    }

    // Save to store
    const response = {
      el_ID: currentElement.value.el_ID,
      el_Type: currentElement.value.el_Type,
      questionId: currentElement.value.el_ID, // For TestMap compatibility
      answered: true, // ✅ Mark as answered for TestMap
      response: responseData.value,
      score: responseData.value.score,
      maxScore: currentElement.value.maxScore,
      correct: responseData.value.correct,
      timestamp: new Date().toISOString(),
      hintUsed: hintUsed.value
    }

    responsesStore.saveResponse(response)
    emit('response-saved', response)

    hasSubmitted.value = true
    showReport.value = true
    currentDate.value = new Date().toLocaleString()

    console.log('GeoGebra response saved:', response)
  } catch (err) {
    console.error('Error submitting GeoGebra response:', err)
    alert('Error submitting response. Please try again.')
  }
}

const closeReport = () => {
  console.log('🔴 Closing GeoGebra report')
  showReport.value = false
}

const resetInteraction = () => {
  showReport.value = false
  showHint.value = false
  hintUsed.value = false
  hasSubmitted.value = false
  responseData.value = {
    answers: '',
    correct: false,
    score: 0,
    base64Data: ''
  }

  if (ggbReady.value) {
    reset()
  }
}

// Initialize GeoGebra
const initGeoGebra = async () => {
  if (!ggbContainer.value || !currentElement.value) {
    console.warn('⚠️ GeoGebra init skipped - missing container or element')
    return
  }

  try {
    const config = {
      ...currentElement.value.ggb_config,
      ggbbase64: currentElement.value.ggb_source?.base64Data || '',
      filename: currentElement.value.ggb_source?.url || '',
      material_id: currentElement.value.ggb_source?.projectId || ''
    }

    console.log('🟢 Initializing GeoGebra with config:', {
      hasBase64: !!config.ggbbase64,
      hasFilename: !!config.filename,
      hasMaterialId: !!config.material_id,
      width: config.width,
      height: config.height
    })

    await initApplet(containerId.value, config)
    console.log('✅ GeoGebra initialized successfully, ggbReady:', ggbReady.value)

    // Load previous response if exists (from restoredAnswer prop)
    if (props.restoredAnswer && Object.keys(props.restoredAnswer).length > 0) {
      hasSubmitted.value = true
      hintUsed.value = props.restoredAnswer.hintUsed || false

      // Restore Base64 if available
      if (props.restoredAnswer.base64Data) {
        // Note: loadFromBase64 would restore student's work
        // For now, we just show it was saved
      }
    }
  } catch (err) {
    console.error('Failed to initialize GeoGebra:', err)
  }
}

// Lifecycle
onMounted(() => {
  nextTick(() => {
    initGeoGebra()
  })
})

onUnmounted(() => {
  removeApplet()
})

watch(() => props.question, (newElement) => {
  if (newElement) {
    currentElement.value = newElement
    nextTick(() => {
      removeApplet()
      initGeoGebra()
    })
  }
}, { deep: true })
</script>

<style scoped>


.geogebra-wrapper {
  border: 2px solid #ddd;
  border-radius: 0.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  min-height: 200px;
}

.geogebra-container {
  width: 100%;
  margin: 0 auto;
}

.readonly-mode {
  pointer-events: none;
  opacity: 0.7;
}

.quill-display {
  margin-bottom: 1rem;
  padding: 0.5rem;
}

.PositiveFeebackContainer {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 0.75rem;
  border-radius: 0.25rem;
  margin-top: 1rem;
}

.NegativeFeebackContainer {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 0.75rem;
  border-radius: 0.25rem;
  margin-top: 1rem;
}

.hintContainer {
  margin-top: 1rem;
}

.hintMessage {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  color: #856404;
  padding: 0.75rem;
  border-radius: 0.25rem;
  margin-top: 0.5rem;
}

.ReportContainer {
  background-color: #202020;
  padding: 1rem;
  margin-top: 1rem;
  color: greenyellow;
  border-radius: 0.5rem;
}

.text-success {
  color: #4caf50;
  font-weight: bold;
}

.text-danger {
  color: #ff6b6b;
  font-weight: bold;
}
</style>
